import { getQueryKey } from '@trpc/react-query';
import { useState } from 'react';
import {
  AvatarGroup,
  Box,
  Button,
  Checkbox,
  styled,
  Typography,
} from '@mui/material';
import { AccountType } from 'further-types/investor';
import { Column, Row } from 'further-ui/components/Layout';
import Avatar from 'further-ui/components/Avatar';
import Table from 'further-ui/components/Table';
import FormControlLabel from 'further-ui/components/FormControlLabel';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { TabType } from '../../ViewInvestor/types';
import { useOnboardingOverviewNavigationContext } from '../../OnboardingOverviewNavigationContext';

const UsersContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#E3F2FD', //TODO: use "blue" palette when delivered by Elias
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.sm),
}));

const UserAvatars = styled(AvatarGroup)(({ theme }) => ({
  '& .MuiAvatar-root': {
    width: 24,
    height: 24,
    fontSize: theme.fontSizes.sm,
  },
}));

type Props = {
  vehicleId: string;
  onClose: () => void;
  investor: {
    id: string;
    name: string;
    accountType: AccountType;
    users: Array<{
      userEmail: string;
    }>;
  };
};

const InvestorDetails: React.FC<Props> = ({ vehicleId, onClose, investor }) => {
  const { goToVehicleApplication } = useOnboardingOverviewNavigationContext();
  const [addWithCommitment, setAddWithCommitment] = useState(false);

  const { data: commitments } = api.onboarding.investorCommitments.useQuery({
    vehicleId,
    investorId: investor.id,
  });

  const userEmails = investor.users.map((user) => user.userEmail);
  const otherUsersCount = userEmails.length - 2;
  const hasMultipleUsers = userEmails.length > 1;

  const usersText = hasMultipleUsers
    ? `The investor users are ${userEmails.join(', ')}${
        otherUsersCount > 0 ? ` and ${otherUsersCount} other(s).` : '.'
      }`
    : userEmails.length === 1
    ? `${userEmails[0]} is the investor user.`
    : 'No investor users found.';

  const hasCommitmentInCurrentVehicle = !!commitments?.find(
    (commitment) => commitment.vehicleId === vehicleId,
  );

  const { mutate: addInvestor, isPending: isAddingInvestor } =
    api.onboarding.addExistingInvestor.useMutation(
      useHandleTrpcMutation<string>()({
        successMessage: 'Investor added successfully.',
        invalidations: [],
        evictions: [getQueryKey(api.onboarding.searchInvestors)],
      }),
    );

  const handleAddInvestor = () => {
    addInvestor(
      {
        vehicleId,
        investorId: investor.id,
        withManualCommitment: addWithCommitment,
      },
      {
        onSuccess: (vehicleApplicationId) => {
          onClose();
          goToVehicleApplication(
            vehicleApplicationId,
            addWithCommitment
              ? TabType.CommitmentDetails
              : TabType.InvestorDetails,
          );
        },
      },
    );
  };

  const userAvatars = (
    <UserAvatars total={userEmails.length}>
      {userEmails.map((email) => (
        <Avatar key={email} color="random" text={email} />
      ))}
    </UserAvatars>
  );

  return (
    <Column fullWidth spacing="lg">
      {!!commitments?.length && (
        <Box position="relative" sx={{ maxHeight: '100%' }}>
          <Table
            rows={commitments}
            columns={[
              {
                label: 'Vehicle',
                key: 'vehicleName',
              },
              {
                label: 'Commitment Date',
                key: 'commitmentDate',
                render: (commitment) => commitment.commitmentDate.toLabel(),
              },
            ]}
          />
        </Box>
      )}
      <Column fullWidth spacing="xl">
        <UsersContainer>
          <Row
            centered
            justify={hasMultipleUsers ? 'space-between' : 'center'}
            spacing="xs"
          >
            {userEmails.length === 1 && userAvatars}
            <Typography variant="h6">{usersText}</Typography>
            {hasMultipleUsers ? userAvatars : null}
          </Row>
        </UsersContainer>
        <Column fullWidth spacing="sm">
          <FormControlLabel
            labelPlacement="start"
            justifyContent="center"
            control={
              <Checkbox
                size="small"
                checked={addWithCommitment || hasCommitmentInCurrentVehicle}
                disabled={hasCommitmentInCurrentVehicle}
                onChange={(_, checked) => {
                  setAddWithCommitment(checked);
                }}
              />
            }
            slotProps={{
              typography: {
                variant: 'body2',
                color: 'grey.900',
              },
            }}
            label="This investor has already completed their subscription"
          />
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleAddInvestor}
            disabled={isAddingInvestor}
          >
            Add investor
          </Button>
        </Column>
      </Column>
    </Column>
  );
};

export default InvestorDetails;
